import { Label } from "@common/ADAPT.Common.Model/organisation/label";
import { Objective } from "@common/ADAPT.Common.Model/organisation/objective";
import { ObjectiveStatus } from "@common/ADAPT.Common.Model/organisation/objective-status";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import moment from "moment";

export enum ClosedFilters {
    ShowSupporting = "Show Only Supporting",
    ShowAll = "Show All",
}

export class ObjectiveFilter {
    public objectiveStatuses = new Set<ObjectiveStatus>([
        ObjectiveStatus.OnTrack,
        ObjectiveStatus.Slow,
        ObjectiveStatus.Stuck,
        ObjectiveStatus.Complete,
    ]);

    private startOfLastYear = new Date(new Date().getFullYear() - 1, 0, 1);
    public fromDate?: Date = this.startOfLastYear;
    public closedOption: ClosedFilters = ClosedFilters.ShowAll;

    // Show External objectives is to show Objectives that are not part of a team/org's objectives but relate to it
    public showSupportingTeamObjectives = false;
    public showSupportedObjectives = true;
    public showAllTeamObjectives = false;
    public assignee?: Person;
    public labels: Label[] = [];

    public setFilterStatuses(tags: ObjectiveStatus[]) {
        this.objectiveStatuses = new Set(tags);
        return this;
    }

    public clone() {
        const clone = new ObjectiveFilter();
        clone.objectiveStatuses = new Set(this.objectiveStatuses);
        clone.fromDate = this.fromDate;
        clone.closedOption = this.closedOption;
        clone.showSupportingTeamObjectives = this.showSupportingTeamObjectives;
        clone.showSupportedObjectives = this.showSupportedObjectives;
        clone.showAllTeamObjectives = this.showAllTeamObjectives;
        clone.assignee = this.assignee;
        clone.labels = [...this.labels];
        return clone;
    }

    public buildObjectivesPredicate(teamId?: number) {
        let predicate: MethodologyPredicate<Objective>;

        if (this.closedOption === ClosedFilters.ShowSupporting && this.objectiveStatuses.has(ObjectiveStatus.Closed)) {
            const clonedFilter = this.clone();
            clonedFilter.objectiveStatuses.delete(ObjectiveStatus.Closed);

            predicate = clonedFilter.buildPredicate(teamId, true);
        } else {
            predicate = this.buildPredicate(teamId);
        }

        return predicate;
    }

    public equals(other: ObjectiveFilter, isTeam: boolean) {
        if (this === other) {
            return true;
        }

        return this.objectiveStatuses.size === other.objectiveStatuses.size
            && Array.from(this.objectiveStatuses).every((s) => other.objectiveStatuses.has(s))
            && ((!this.fromDate && !other.fromDate)
                || (!!this.fromDate && !!other.fromDate && this.fromDate.getTime() === other.fromDate.getTime()))
            && this.closedOption === other.closedOption
            && (this.showSupportingTeamObjectives === other.showSupportingTeamObjectives || !!isTeam)
            && (this.showAllTeamObjectives === other.showAllTeamObjectives || !!isTeam)
            && (this.showSupportedObjectives === other.showSupportedObjectives || !isTeam)
            && this.assignee === other.assignee
            && ArrayUtilities.contentsAreEqual(this.labelIds, other.labelIds);
    }

    public buildPredicate(teamId?: number, showSupportingClosed?: boolean) {
        const predicate = new MethodologyPredicate<Objective>();

        const includeAll = !teamId && this.showAllTeamObjectives;
        if (!includeAll) {
            // if include all, all supporting objectives are already included
            const objectiveRootPredicate = new MethodologyPredicate<Objective>("teamId", "==", teamId ?? null);
            if (teamId ? this.showSupportedObjectives : this.showSupportingTeamObjectives) {
                // Walk up the ancestor chain to find objectives whose parent (at any depth) belongs to this team.
                // Builds: parentObjId != null AND (parentObj.teamId == teamId OR (parentObj.parentObjId != null AND (parentObj.parentObj.teamId == teamId OR (...))))
                // Built from deepest level outward so .or() is always called on a leaf (OR) predicate, not on an AND predicate.
                const maxDepth = 5;
                const parentPathAt = (depth: number) => Array(depth).fill("parentObjective").join(".");

                // Deepest level: parentObjectiveId != null AND teamId == teamId
                let chain = new MethodologyPredicate<Objective>(`${parentPathAt(maxDepth - 1)}.parentObjectiveId` as any, "!=", null)
                    .and(new MethodologyPredicate<Objective>(`${parentPathAt(maxDepth)}.teamId` as any, "==", teamId ?? null));

                // Wrap outward: each level adds parentPath.parentObjectiveId != null AND (parentPath.teamId == teamId OR <inner>)
                for (let depth = maxDepth - 1; depth >= 1; depth--) {
                    const entityName = depth === 1
                        ? "parentObjectiveId"
                        : `${parentPathAt(depth - 1)}.parentObjectiveId`;

                    chain = new MethodologyPredicate<Objective>(entityName as any, "!=", null)
                        .and(new MethodologyPredicate<Objective>(`${parentPathAt(depth)}.teamId` as any, "==", teamId ?? null).or(chain));
                }

                objectiveRootPredicate.or(chain);
            }

            predicate.and(objectiveRootPredicate);
        }

        if (this.objectiveStatuses.size > 0) {
            const statusPredicate = new MethodologyPredicate<Objective>("status", "in", [...this.objectiveStatuses]);

            if (showSupportingClosed) {
                const closedSupportPredicate = new MethodologyPredicate<Objective>("status", "==", ObjectiveStatus.Closed);
                closedSupportPredicate.and(new MethodologyPredicate<Objective>("parentObjectiveId", "!=", null));
                closedSupportPredicate.and(new MethodologyPredicate<Objective>("parentObjective.status", "!=", ObjectiveStatus.Closed));

                statusPredicate.or(closedSupportPredicate);
            }

            predicate.and(statusPredicate);
        }

        if (this.fromDate) {
            const startOfYear = moment(this.fromDate)
                .startOf("year")
                .toDate();
            predicate.and(new MethodologyPredicate<Objective>("dueDate", ">=", startOfYear));
        } else {
            predicate.and(new MethodologyPredicate<Objective>("dueDate", ">=", this.startOfLastYear));
        }

        if (this.assignee) {
            predicate.and(new MethodologyPredicate<Objective>("assigneePersonId", "==", this.assignee.personId));
        }

        return predicate;
    }

    public get labelIds() {
        return this.labels.map((l) => l.labelId);
    }

    /**
     * When the filter has the Closed status only and 'Closed Objectives' == 'Show Only Supporting',
     * the query will always return everything, so this function allows us to easily check this condition and return quickly
     */
    public get onlyClosedAndSupporting() {
        return this.objectiveStatuses.has(ObjectiveStatus.Closed)
            && this.objectiveStatuses.size === 1
            && this.closedOption === ClosedFilters.ShowSupporting;
    }
}
